import { useRef, useState } from "react";
import type { Movie } from "@/types/movie";

type PlayerStatus = "idle" | "loading" | "error";

interface PlayerDemoProps {
  movie: Movie;
  episodeLabel?: string;
}

export default function PlayerDemo({ movie, episodeLabel }: PlayerDemoProps) {
  const [status, setStatus] = useState<PlayerStatus>("idle");
  const timerRef = useRef<number | null>(null);

  const startPlay = () => {
    setStatus("loading");
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setStatus("error"), 1600);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-background-200/80 bg-foreground-950">
      <div className="relative aspect-video w-full">
        <img
          src={movie.banner || movie.cover}
          alt={`${movie.title} 播放画面`}
          className="h-full w-full object-cover opacity-40"
        />

        {status === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center">
            <button
              type="button"
              onClick={startPlay}
              aria-label="播放演示视频"
              className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-105"
            >
              <i className="ri-play-fill text-3xl"></i>
            </button>
            <p className="text-sm text-background-200">
              点击播放（演示播放器，暂未接入真实片源）
            </p>
          </div>
        )}

        {status === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
            <span className="h-10 w-10 animate-spin rounded-full border-2 border-background-50/30 border-t-primary-400"></span>
            <p className="text-sm text-background-200">正在加载播放源…</p>
          </div>
        )}

        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50/10 text-accent-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </span>
            <p className="text-sm font-medium text-background-50">
              暂时无法播放
            </p>
            <p className="max-w-sm text-xs text-background-300">
              当前为演示播放器，真实播放接口尚未接入。接入后端后即可正常观看。
            </p>
            <button
              type="button"
              onClick={startPlay}
              className="mt-1 flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
            >
              <i className="ri-refresh-line text-base"></i>
              重新加载
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-50/10 bg-foreground-950 px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-background-50">
            {movie.title}
            {episodeLabel ? ` · ${episodeLabel}` : ""}
          </p>
          <p className="mt-0.5 text-xs text-background-400">
            分辨率 1080P · 演示模式 · 无真实播放状态
          </p>
        </div>
        <div className="flex items-center gap-2 text-background-300">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-background-50/10">
            <i className="ri-volume-up-line text-base"></i>
          </span>
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-background-50/10">
            <i className="ri-fullscreen-line text-base"></i>
          </span>
        </div>
      </div>
    </div>
  );
}